<template>
  <div
    class="zm-group-select-dropdown zm-popper"
    :style="{width:width}"
    tabindex="0"
    @keydown="handleKeyDown"
  >
    <zm-scrollbar
      wrap-class="zm-group-select-dropdown__wrap"
      :wrap-style="wrapStyl"
      ref="scrollbar"
      @scroll-end="onScrollEnd"
    >
      <div
        v-if="!emptyText && options.length"
        class="zm-group-select-dropdown__header"
      >
        <div
          v-for="column in groupColumns"
          :key="column.prop"
          :style="{width: column.width+'px'}"
          class="zm-group-select-dropdown__header-item"
        >
          <zm-checkbox
            v-if="column.type==='checkbox'"
            :value="isAllSelected"
            @change="toggleAllSelection"
            :indeterminate="indeterminate"
            aria-label="Select All"
            class="mgl-xs"
          />
          <span v-else>
            {{ column.name }}
          </span>
        </div>
      </div>
      <div
        class="zm-group-select-dropdown__item"
        :class="{'zm-group-select-dropdown__divided': !noDivided}"
        v-for="item in options"
        :key="item.id"
      >
        <div
          v-for="column in groupColumns"
          :key="column.prop"
          :style="{width: column.width+'px'}"
          class="zm-group-select-dropdown__content pdr-xs"
        >
          <zm-checkbox
            v-if="column.type==='checkbox'"
            :value="item.selected"
            @change="toggleSelection(item)"
            aria-label="Select this row"
            class="mgl-xs"
          />
          <span v-else>
            {{ item[column.prop] }}
          </span>
        </div>
      </div>
      <div v-if="loading" v-loading="loading" class="zm-group-select-dropdown__loading">
      </div>
      <div v-if="emptyText" class="zm-group-select-dropdown__empty">
        {{ emptyText }}
      </div>
    </zm-scrollbar>
  </div>
</template>

<script type="text/babel">
  import ZmCheckbox from '../checkbox/';
  import ZmScrollbar from '../scrollbar';
  import Popper from '../../utils/vue-popper';
  import scrollbarWidth from '../../utils/scrollbar-width';
  import { debounce } from 'throttle-debounce';
  import { addResizeListener, removeResizeListener } from '../../utils/resize-event';
  import AriaUtils from '../../utils/aria-utils';
  import NavigationMixin from './navigation-mixin';

  export default {
    name: 'ZmGroupSelectDropdown',

    componentName: 'ZmGroupSelectDropdown',

    components: {
      ZmCheckbox,
      ZmScrollbar
    },

    mixins: [Popper, NavigationMixin],

    props: {
      placement: {
        type: String,
        default: 'bottom-start'
      },
      boundariesPadding: {
        type: Number,
        default: 0
      },
      popperOptions: {
        type: Object,
        default() {
          return {
            gpuAcceleration: false
          };
        }
      },
      noDataText: String,
      options: Array,
      loading: Boolean,
      width: String,
      columns: Array,
      checkboxColumnWidth: Number,
      noDivided: Boolean
    },

    data() {
      return {
        groupColumns: []
      };
    },

    computed: {
      wrapStyl() {
        const gutter = scrollbarWidth();
        const maxHeight = gutter ? '339px' : '324px';
        return `max-height:${maxHeight};`;
      },
      indeterminate() {
        return this.selected.length > 0 && !this.isAllSelected;
      },
      selected() {
        const ret = [];
        this.options.forEach((item) => {
          if (item.selected) {
            ret.push(item);
          }
        });
        return ret;
      },
      isAllSelected() {
        return this.options.length > 0 &&
          this.selected.length === this.options.length;
      },
      emptyText() {
        if (!this.loading && this.options.length === 0) {
          return this.noDataText;
        }
        return null;
      }
    },

    watch: {
      columns: {
        immediate: true,
        handler(newVal) {
          if (newVal && newVal.length) {
            this.createColumns(newVal);
          }
        }
      }
    },

    methods: {
      toggleAllSelection() {
        if (this.isAllSelected) {
          this.clearAll();
        } else {
          this.selectAll();
        }
        this.$emit('change', this.selected);
      },
      selectAll() {
        this.options.forEach((item) => {
          this.$set(item, 'selected', true);
        });
      },
      clearAll() {
        this.options.forEach((item) => {
          this.$set(item, 'selected', false);
        });
      },
      toggleSelection(item) {
        this.$set(item, 'selected', !item.selected);
        this.$emit('change', item);
      },
      onScrollEnd() {
        if (!this.loading) {
          this.$emit('scroll-end');
        }
      },
      handleKeyDown(event) {
        const keyCode = event.keyCode;
        const keys = AriaUtils.keys;
        switch (keyCode) {
          case keys.up:
            this.navigateCheckbox('prev');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.down:
            this.navigateCheckbox('next');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.home:
            this.navigateCheckbox('home');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.end:
            this.navigateCheckbox('end');
            event.preventDefault();
            event.stopPropagation();
            break;
          case keys.tab:
          case keys.esc:
            this.$parent.close();
            this.$parent.focus();
            event.preventDefault();
            event.stopPropagation();
            break;
        }
      },
      createColumns(columns) {
        this.groupColumns = [];
        columns.forEach((column) => {
          this.groupColumns.push({
            type: 'data',
            name: column.name,
            prop: column.prop,
            width: column.width,
            flex: !column.width
          });
        });
        this.groupColumns.unshift({
          type: 'checkbox',
          width: this.checkboxColumnWidth,
          flex: false
        });
        this.layout();
      },
      layout() {
        if (!this.$el || !this.groupColumns.length) {
          return;
        }
        const totalWidth = this.$el.clientWidth - 48; // margin
        let occupyWidth = 0;
        let flexCount = 0;
        this.groupColumns.forEach((column) => {
          if (!column.flex) {
            occupyWidth += column.width;
          } else {
            flexCount++;
          }
        });
        const restWidth = totalWidth - occupyWidth;
        const flexWidth = restWidth / flexCount | 0;
        const gapWidth = restWidth - flexWidth * flexCount;
        let handleGap = false;
        for (let i = this.groupColumns.length - 1; i > 0; i--) {
          const column = this.groupColumns[i];
          if (column.flex) {
            column.width = flexWidth;
            if (!handleGap) {
              handleGap = true;
              column.width += gapWidth;
            }
          }
        }
      },
      bindEvents() {
        addResizeListener(this.$el, this.debounceLayout);
      },

      unbindEvents() {
        removeResizeListener(this.$el, this.debounceLayout);
      }
    },

    created() {
      this.debounceLayout = debounce(50, () => this.layout());
    },

    mounted() {
      this.bindEvents();
      this.layout();
      this.referenceElm = this.$parent.$el;
      this.$parent.popperElm = this.popperElm = this.$el;
      this.$on('updatePopper', () => {
        if (this.$parent.visible) {
          this.$nextTick(this.updatePopper);
        }
      });
      this.$on('destroyPopper', this.destroyPopper);
    },

    beforeDestroy() {
      this.unbindEvents();
    }
  };
</script>

<style lang="scss">
  @import "../../scss/common/popper";
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";

  @include b(group-select-dropdown) {
    outline: 0;
    position: absolute;
    z-index: #{$--index-top + 1};
    margin: 0;
    margin-top: 4px;
    max-height: 340px;
    border-radius: $--select-dropdown-border-radius;
    background-color: $--select-dropdown-background;
    box-shadow: $--select-dropdown-shadow;
    box-sizing: border-box;

    .zm-checkbox {
      position: relative;
      top: 1px;
      line-height: 1;
    }

    @include e(header) {
      display: flex;
      align-items: center;
      height: 57px;
      border-bottom: 1px solid $--color-border-light;
      font-weight: bold;
      color: $--color-text-secondary;
    }

    @include e(header-item) {
      display: inline-block;
    }

    @include e(item) {
      display: flex;
      align-items: center;
      height: 45px;

      &:last-child {
        border-bottom: none;
      }
    }

    @include e(divided) {
      border-bottom: 1px solid $--color-border-light;
    }

    @include e(empty) {
      padding: $--select-dropdown-empty-padding;
      margin: 0;
      text-align: center;
      color: $--select-dropdown-empty-color;
      font-size: $--select-font-size;
    }

    @include e(loading) {
      height: 60px;
      margin-top: 8px;
      z-index: -1;

      .zm-loading-spinner {
        .circular {
          height: 30px;
          width: 30px;
        }
      }
    }

    @include e(text) {
      height: 60px;
      text-align: center;
    }

    @include e(wrap) {
      padding: 0 12px;
    }

    @include e(content) {
      display: inline-block;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  .zm-theme--dark {
    @include b(group-select-dropdown) {
      background-color: #131619;
      box-shadow: 0px 6px 12px 0px black, 0px 12px 24px 0px black;
      border: 1px solid #444b53;
      @include e(item) {
        border-color: #252a30;
      }
    }
  }

  .is-keyboard-event {
    .zm-group-select-dropdown {
      &:focus {
        outline: 1px solid $--color-primary;
      }
    }
  }
</style>
